import { useMutation, useQuery } from "convex/react";
import { useEffect, useMemo, useState } from "react";
import {
  claimRestrictedSupportCase,
  confirmSupportSpecialistHandoff,
  listRestrictedSupportCases,
  releaseRestrictedSupportCase,
  revealRestrictedSupportContact,
} from "../../backend/functions";
import {
  AdministrationImpactPanel,
  AdministrationPageHeader,
  AdministrationStatusSummary,
  AdministrationWorkSurface,
} from "../../ui/administration-surface";

function reference(prefix: string): string {
  return `${prefix}_${crypto.randomUUID().replaceAll("-", "")}`;
}

export function RestrictedSupportCasesPanel(): React.JSX.Element {
  const correlationId = useMemo(() => reference("corr_restricted_queue"), []);
  const rows = useQuery(listRestrictedSupportCases, { correlationId });
  const claim = useMutation(claimRestrictedSupportCase);
  const confirm = useMutation(confirmSupportSpecialistHandoff);
  const release = useMutation(releaseRestrictedSupportCase);
  const reveal = useMutation(revealRestrictedSupportContact);
  const [selected, setSelected] = useState<NonNullable<typeof rows>[number] | null>(null);
  const [contact, setContact] = useState<{ value: string; hideAt: number } | null>(null);
  const [feedback, setFeedback] = useState<string | null>(null);

  useEffect(() => {
    if (contact === null) return;
    const handle = window.setTimeout(
      () => setContact(null),
      Math.max(0, contact.hideAt - Date.now()),
    );
    return () => window.clearTimeout(handle);
  }, [contact]);

  const claimRow = async (row: NonNullable<typeof rows>[number]): Promise<void> => {
    setFeedback(null);
    try {
      await claim({
        correlationId: reference("corr_restricted_claim"),
        requestKey: reference("cmd_restricted_claim"),
        caseCode: row.caseCode,
        expectedAccessEpoch: row.accessEpoch,
      });
      setSelected(row);
      setFeedback("Restricted access is assigned to you for this review only.");
    } catch {
      setFeedback("This restricted case changed or is not available to you.");
    }
  };

  return (
    <section className="support-workspace">
      <AdministrationPageHeader
        title="Restricted cases"
        purpose="Review specialist work with minimum necessary access. Private content stays withheld until current authority is confirmed."
        status={{ label: "Restricted access", tone: "monitor" }}
      />
      {rows === undefined ? null : (
        <>
          <AdministrationStatusSummary
            items={[
              {
                label: "Needs action",
                tone: "action",
                value: rows.length,
                title: "Restricted reviews",
                description: "Claim only the case you are authorised and ready to review.",
              },
              {
                label: "Monitor",
                tone: "monitor",
                value: selected === null ? 0 : 1,
                title: "Purpose-bound access in use",
                description: "Access ends when the review is released or replaced.",
              },
              {
                label: "Healthy",
                tone: "healthy",
                value: rows.length === 0 ? 1 : 0,
                title: "Queue clear",
                description: "Shown only when no restricted review is waiting.",
              },
            ]}
          />
          <AdministrationImpactPanel
            label="Privacy boundary"
            title="Private content stays withheld until current authority is confirmed"
            description="Review one case with minimum necessary access. Never copy customer content into Jira or operational evidence."
            action={
              selected === null && rows[0] !== undefined ? (
                <button type="button" onClick={() => void claimRow(rows[0]!)}>
                  Review next case safely
                </button>
              ) : undefined
            }
            tone={rows.length > 0 ? "monitor" : "healthy"}
          />
        </>
      )}
      {feedback === null ? null : (
        <p role="status" className="support-feedback">
          {feedback}
        </p>
      )}
      {rows === undefined ? <p>Loading restricted cases…</p> : null}
      {rows?.length === 0 ? <p className="empty-state">No restricted cases need review.</p> : null}
      {selected === null ? (
        rows?.map((row) => (
          <article className="support-row" key={row.caseCode}>
            <div>
              <strong>{row.caseCode}</strong>
              <span>{row.specialistState} specialist review</span>
            </div>
            <button type="button" onClick={() => void claimRow(row)}>
              Review safely
            </button>
          </article>
        ))
      ) : (
        <AdministrationWorkSurface
          id="restricted-case-review"
          className="support-thread"
          title="Protected conversation"
          purpose="Complete one specialist review using the minimum information necessary."
          state={{ label: "Access assigned to you", tone: "monitor" }}
          consequence="Confirming the handoff records specialist ownership. Revealing contact shows it for 60 seconds. Releasing the case ends this access and creates a safe replacement thread."
        >
          <button
            type="button"
            className="secondary-action"
            onClick={() => {
              setSelected(null);
              setContact(null);
            }}
          >
            Back to restricted queue
          </button>
          <p className="eyebrow">{selected.caseCode}</p>
          <p>
            The subject and messages are intentionally withheld. Do not copy customer content into
            Jira or operational evidence.
          </p>
          <div className="support-actions">
            <button
              className="primary-action"
              type="button"
              onClick={() =>
                void confirm({
                  correlationId: reference("corr_handoff_confirm"),
                  requestKey: reference("cmd_handoff_confirm"),
                  caseCode: selected.caseCode,
                  expectedWorkflowRevision: selected.workflowRevision,
                })
                  .then(() => setFeedback("Specialist handoff confirmed."))
                  .catch(() => setFeedback("The specialist handoff could not be confirmed."))
              }
            >
              Confirm specialist handoff
            </button>
            <button
              className="secondary-action"
              type="button"
              onClick={() =>
                void reveal({
                  correlationId: reference("corr_contact_reveal"),
                  caseCode: selected.caseCode,
                  purpose: "verified-access-assistance",
                })
                  .then((value) =>
                    setContact({ value: value.value, hideAt: value.hideAtEpochMilliseconds }),
                  )
                  .catch(() =>
                    setFeedback(
                      "A verified contact is not available or fresh authority is required.",
                    ),
                  )
              }
            >
              Reveal verified contact for 60 seconds
            </button>
            <button
              className="caution-action"
              type="button"
              onClick={() =>
                void release({
                  correlationId: reference("corr_restricted_release"),
                  requestKey: reference("cmd_restricted_release"),
                  caseCode: selected.caseCode,
                  expectedWorkflowRevision: selected.workflowRevision,
                  reason: "specialist-review-complete",
                })
                  .then(() => {
                    setSelected(null);
                    setContact(null);
                    setFeedback("Restricted review completed with a safe replacement thread.");
                  })
                  .catch(() =>
                    setFeedback("Confirm specialist review and refresh the case before release."),
                  )
              }
            >
              Release to a safe replacement thread
            </button>
          </div>
          {contact === null ? null : (
            <p className="support-contact" role="status">
              <strong>Verified email:</strong> {contact.value}
              <br />
              <span>This value hides automatically and is not written to evidence.</span>
            </p>
          )}
        </AdministrationWorkSurface>
      )}
    </section>
  );
}
